import { isEmpty, notNull } from "@utils/var.check.ts";
import {
  MODULE_COMPLETION_STATUS,
  ModuleCompletionStatus,
  SectionDisplayModel,
} from "@/common/section-display-types.ts";
import StringUtils from "@utils/string.utils.ts";

/**
 * Generic i18n translate function signature (compatible with `vue-i18n`'s `t`).
 * Passed in explicitly so these functions stay pure and unit-testable in isolation
 */
export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * Resolves the section line displayed on a module/resource card or table row,
 * per the canonical "Section display logic" table (technical-design.md §2.3):
 *
 * | Module status | Section shown            |
 * |----------------|---------------------------|
 * | INCOMPLETE     | first incomplete section  |
 * | COMPLETE       | last section              |
 * | PAUSED         | none ("Module is paused" text when the module has no sections) |
 *
 * BE already selects the first-incomplete/last section server-side —
 * this function only decides WHAT TEXT to render, it never re-derives the selection.
 *
 * Resolution order once status/hasSections/isFullModuleAttached checks pass:
 * 1. `sectionCoverHidden` (section "viewable" toggle OFF) -> hide the entire line (blank).
 * 2. `sectionNumberHidden` is `false` -> show "Section {num}".
 * 3. `sectionNumberHidden` is `true` AND `selectedSectionNumber` is truthy -> fall through to the section title (or blank if no title).
 *
 * @param model Canonical section-display model (see `SectionDisplayModel`).
 * @param translate i18n translate function, e.g. `useI18n().t`.
 * @returns The text to render, or `null` when nothing should be shown.
 */
export function resolveSectionDisplayText(
  model: SectionDisplayModel,
  translate: TranslateFn,
): string | null {
  if (model.status === MODULE_COMPLETION_STATUS.PAUSED) {
    return model.hasSections ? null : translate("module_is_paused");
  }

  if (!model.hasSections) return null;

  // Planner-only exclusion rule (AC#106/#107) — isolated here so it can be
  // adjusted independently of the canonical #105/#203 logic (impact-assessment §7).
  if (model.isFullModuleAttached === true) return null;

  // Section-level "viewable" toggle is OFF -> hide the entire line, no title fallback.
  if (model.sectionCoverHidden || !model.selectedSectionNumber) return null;

  if (!model.sectionNumberHidden) {
    const sectionLetter = StringUtils.getAlphabeticalLetter(model.selectedSectionNumber - 1);
    return translate("section_with_number", { num: sectionLetter });
  }

  if (model.sectionNumberHidden && !isEmpty(model.selectedSectionTitle)) {
    return model.selectedSectionTitle as string;
  }

  // Section number hidden and no title available -> render nothing (blank).
  return null;
}

export interface LastAccessedDisplayParams {
  /**
   * Date-only string already formatted by the caller (e.g. via
   * `DateTimeFormatter.formatLocalDateTimeToDateOnly`). This function never parses
   * raw date values itself since callers may receive different raw date formats
   * depending on context (self-study pending action vs. table view item).
   */
  formattedDate?: string | null;
  /** Whether the student has ever attempted/accessed this resource. */
  hasAttempted?: boolean;
  /** `false` when the underlying resource no longer exists. */
  lessonExists?: boolean;
}

/**
 * Resolves the "Last Accessed" field display value:
 * - Resource no longer exists -> "—"
 * - Never attempted -> "Not yet accessed"
 * - Attempted -> date only (no time) — the caller is responsible for formatting
 *   the date without the time portion and for using the FULL timestamp for sorting.
 */
export function resolveLastAccessedDisplay(
  params: LastAccessedDisplayParams,
  translate: TranslateFn,
): string {
  if (params.lessonExists === false) return "—";
  if (!params.hasAttempted || isEmpty(params.formattedDate)) {
    return translate("not_yet_accessed");
  }
  return params.formattedDate as string;
}

export interface RawSectionDisplayModel {
  id?: number | null;
  pageNo?: number | null;
  title?: string | null;
  /** Self-study only (`SectionPendingActionDisplayModel`): "COMPLETE" | "INCOMPLETE". */
  status?: string | null;
}

export interface RawSectionInfo {
  hideSectionActivityNumber?: boolean | null;
  hideSectionCover?: boolean | null;
  sectionDisplayModel?: RawSectionDisplayModel | null;
}

export interface RawPendingActionForCompletion {
  paused?: boolean | null;
  progressStatus?: string | null;
}

/**
 * Derives the module's `ModuleCompletionStatus` from the raw BE payload.
 * `paused` always wins; otherwise falls back to the self-study section's own
 * `status` field, then the pending action's `progressStatus`.
 */
export function deriveCompletionStatus(
  pendingAction?: RawPendingActionForCompletion | null,
  sectionDisplayModel?: RawSectionDisplayModel | null,
): ModuleCompletionStatus {
  if (pendingAction?.paused) return MODULE_COMPLETION_STATUS.PAUSED;
  if (sectionDisplayModel?.status === "COMPLETE" || pendingAction?.progressStatus === "Completed") {
    return MODULE_COMPLETION_STATUS.COMPLETE;
  }
  return MODULE_COMPLETION_STATUS.INCOMPLETE;
}

/**
 * Maps the raw BE `sectionInfo`/`pendingAction` shapes into the canonical
 * `SectionDisplayModel` consumed by `resolveSectionDisplayText()`.
 *
 * `hasSections` defaults to `true` when `sectionDisplayModel.id` is absent
 * (graceful degradation while SLS-12369 is rolling out) and is only `false`
 * when BE explicitly returns the empty-section sentinel (`id === 0`).
 *
 * `hideSectionActivityNumber` (module-level setting) and `hideSectionCover`
 * (section-level "viewable" toggle) are two DISTINCT flags and must be mapped
 * independently — do NOT conflate them (see `SectionDisplayModel` doc comment).
 */
export function mapToSectionDisplayModel(
  sectionInfo?: RawSectionInfo | null,
  pendingAction?: RawPendingActionForCompletion | null,
  isFullModuleAttached?: boolean,
): SectionDisplayModel {
  const displayModel = sectionInfo?.sectionDisplayModel ?? null;
  const hasSections = notNull(displayModel) && displayModel?.id !== 0;

  return {
    status: deriveCompletionStatus(pendingAction, displayModel),
    hasSections,
    sectionNumberHidden: !!sectionInfo?.hideSectionActivityNumber,
    sectionCoverHidden: !!sectionInfo?.hideSectionCover,
    selectedSectionNumber: displayModel?.pageNo ?? null,
    selectedSectionTitle: displayModel?.title ?? null,
    isFullModuleAttached,
  };
}

export interface RawLastAccessedSource {
  lastAccessedDate?: string | null;
  lastAttemptedDate?: string | null;
  lessonExists?: boolean | null;
}

/**
 * Maps a raw pending-action payload into `LastAccessedDisplayParams`, delegating
 * the actual date-only formatting to the caller-supplied `formatDateOnly` (since
 * different producers may hand back different raw date formats).
 */
export function mapToLastAccessedParams(
  pendingAction: RawLastAccessedSource | null | undefined,
  formatDateOnly: (rawDate: string) => string,
): LastAccessedDisplayParams {
  const rawDate = pendingAction?.lastAccessedDate ?? pendingAction?.lastAttemptedDate ?? null;
  return {
    formattedDate: isEmpty(rawDate) ? null : formatDateOnly(rawDate as string),
    hasAttempted: !isEmpty(rawDate),
    lessonExists: pendingAction?.lessonExists ?? true,
  };
}
